import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  ensureCreationLabel,
  fetchCreationImageBlob,
  fetchExportImageBlob,
  getCreationDetail,
  getExportDetail,
} from "../../services/api/editor/background_removal";
import {
  readPersistedCreationId,
  useEditorStore,
} from "../../state/editorStore";
import { isFallbackClassifyLabel } from "../../utils/classifyImageLabel";
import { applyObjectLayerLabel } from "../../utils/registerLibraryUploadOnce";
import logger from "../../utils/logger";

function resolveCreationId(creationIdParam, storeCreationId) {
  const rawParam = (creationIdParam || "").trim();
  const paramCid = rawParam ? Number(rawParam) : null;
  if (paramCid && Number.isFinite(paramCid) && paramCid > 0) {
    return paramCid;
  }
  if (storeCreationId && Number(storeCreationId) > 0) {
    return Number(storeCreationId);
  }
  return readPersistedCreationId();
}

async function prefetchTransparentSource(token, transparentSourceId) {
  if (!transparentSourceId || !token) return null;
  try {
    const blob = await fetchCreationImageBlob(token, transparentSourceId);
    return URL.createObjectURL(blob);
  } catch (err) {
    logger.warn("Could not prefetch transparent source", err);
    return null;
  }
}

/**
 * Restore editor image from ?creationId= or ?exportId= (My Designs open).
 */
export function useEditorSessionRestore({
  shareIdParam,
  isDemoSample = false,
  isFreshUpload = false,
  isLoading,
  selectedFile,
  processedImage,
  getToken,
  isAuthLoaded,
  setProcessedImage,
  setCreationId,
  setPassportProcessedImage,
  setPassportCreationId,
  onCreationMeta,
  setBgRemovedImageUrl,
  setRemoveBackgroundActive,
  preferRemoveBackgroundOn = false,
  navigate,
  creationIdParam,
  exportIdParam,
}) {
  const location = useLocation();
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState(null);
  const [hasHydrated, setHasHydrated] = useState(() =>
    useEditorStore.persist.hasHydrated()
  );

  const storeCreationId = useEditorStore((s) => s.creationId);
  const setStoreCreationId = useEditorStore((s) => s.setCreationId);
  const setLineage = useEditorStore((s) => s.setLineage);
  const commitImageSession = useEditorStore((s) => s.commitImageSession);

  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  useEffect(() => {
    if (useEditorStore.persist.hasHydrated()) {
      setHasHydrated(true);
      return undefined;
    }
    return useEditorStore.persist.onFinishHydration(() => {
      setHasHydrated(true);
    });
  }, []);

  useEffect(() => {
    if (!hasHydrated || shareIdParam) return;
    const raw = (creationIdParam || "").trim();
    if (!raw) return;
    const urlCid = Number(raw);
    if (!Number.isFinite(urlCid) || urlCid <= 0) return;
    if (storeCreationId !== urlCid) {
      setStoreCreationId(urlCid);
    }
  }, [hasHydrated, shareIdParam, creationIdParam, storeCreationId, setStoreCreationId]);

  const exportRawForGate = (exportIdParam || "").trim();
  const creationRawForGate = (creationIdParam || "").trim();
  const hasUrlRestoreTarget =
    (exportRawForGate && Number(exportRawForGate) > 0) ||
    (creationRawForGate && Number(creationRawForGate) > 0);

  useEffect(() => {
    if (
      isDemoSample ||
      isFreshUpload ||
      !isAuthLoaded ||
      shareIdParam ||
      (selectedFile && !hasUrlRestoreTarget)
    ) {
      return;
    }
    if (processedImage) {
      return;
    }

    const exportRaw = (exportIdParam || "").trim();
    const exportId = exportRaw ? Number(exportRaw) : null;
    const cid = exportId ? null : resolveCreationId(creationIdParam, storeCreationId);

    if (!cid && !exportId) {
      return;
    }

    let cancelled = false;
    setRestoring(true);
    setRestoreError(null);

    (async () => {
      try {
        const token = await getTokenRef.current();
        if (!token) {
          throw new Error("Sign in to restore your editor session.");
        }

        if (exportId) {
          logger.info("Opening export in editor", { exportId });
          const meta = await getExportDetail(token, exportId);
          const blob = await fetchExportImageBlob(token, exportId);
          if (cancelled) return;

          const blobUrl = URL.createObjectURL(blob);
          const transparentSourceId = meta.transparent_source_id ?? null;
          const rootCreationId = meta.root_creation_id ?? null;
          const transparentBlob = await prefetchTransparentSource(
            token,
            transparentSourceId
          );
          if (cancelled) return;

          commitImageSession(meta.creation_id ?? null, blobUrl, {
            transparentSourceId,
            rootCreationId,
          });
          setLineage({
            creationId: meta.creation_id ?? null,
            transparentSourceId,
            rootCreationId,
          });
          setProcessedImage(blobUrl);
          setCreationId(meta.creation_id ?? null);
          if (transparentBlob && setBgRemovedImageUrl && meta.has_transparency) {
            setBgRemovedImageUrl(transparentBlob);
          }
          if (setRemoveBackgroundActive) {
            setRemoveBackgroundActive(
              preferRemoveBackgroundOn || Boolean(meta.has_transparency)
            );
          }
          return;
        }

        logger.info("Restoring editor session", { creationId: cid });
        const meta = await getCreationDetail(token, cid);
        const isPassportCreation = meta.file_type === "passport_photo";
        if (onCreationMeta) {
          onCreationMeta(meta, isPassportCreation);
        }

        const blob = await fetchCreationImageBlob(token, cid);
        if (cancelled) return;

        const blobUrl = URL.createObjectURL(blob);
        const transparentSourceId =
          meta.transparent_source_id ?? meta.creation_id ?? cid;
        const rootCreationId = meta.root_creation_id ?? transparentSourceId;
        const transparentBlob = await prefetchTransparentSource(
          token,
          transparentSourceId
        );
        if (cancelled) return;

        commitImageSession(cid, blobUrl, {
          transparentSourceId,
          rootCreationId,
        });
        setLineage({
          creationId: cid,
          transparentSourceId,
          rootCreationId,
        });
        const applyProcessedImage = isPassportCreation && setPassportProcessedImage
          ? setPassportProcessedImage
          : setProcessedImage;
        const applyCreationId = isPassportCreation && setPassportCreationId
          ? setPassportCreationId
          : setCreationId;
        applyProcessedImage(blobUrl);
        applyCreationId(cid);
        if (transparentBlob && setBgRemovedImageUrl) {
          // Only treat as cutout when the creation actually has transparency.
          if (meta.has_transparency) {
            setBgRemovedImageUrl(transparentBlob);
          }
        }
        if (setRemoveBackgroundActive) {
          setRemoveBackgroundActive(
            preferRemoveBackgroundOn || Boolean(meta.has_transparency)
          );
        }

        // Object / Layers name from stored display_label (or lazy classify+persist).
        const metaLabel =
          typeof meta.label === "string" && meta.label.trim()
            ? meta.label.trim()
            : null;
        if (metaLabel && !isFallbackClassifyLabel(metaLabel)) {
          applyObjectLayerLabel(metaLabel);
        } else {
          const labelTarget =
            meta.root_creation_id ?? meta.creation_id ?? cid;
          void (async () => {
            try {
              const ensured = await ensureCreationLabel(token, labelTarget);
              const label =
                typeof ensured?.label === "string" && ensured.label.trim()
                  ? ensured.label.trim()
                  : null;
              if (label && !isFallbackClassifyLabel(label)) {
                applyObjectLayerLabel(label);
              }
            } catch (err) {
              logger.warn("ensureCreationLabel on restore failed", err);
            }
          })();
        }

        const currentParam = (creationIdParam || "").trim();
        if (!currentParam || Number(currentParam) !== cid) {
          navigate(`/editor?creationId=${cid}`, {
            replace: true,
            state: location.state || null,
          });
        }
      } catch (err) {
        if (!cancelled) {
          logger.error("Editor session restore failed", err);
          setRestoreError(
            err?.message || "Could not restore your previous editor session."
          );
        }
      } finally {
        if (!cancelled) {
          setRestoring(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isDemoSample,
    isFreshUpload,
    isAuthLoaded,
    shareIdParam,
    selectedFile,
    hasUrlRestoreTarget,
    processedImage,
    storeCreationId,
    creationIdParam,
    exportIdParam,
    setProcessedImage,
    setCreationId,
    setPassportProcessedImage,
    setPassportCreationId,
    onCreationMeta,
    setBgRemovedImageUrl,
    setRemoveBackgroundActive,
    preferRemoveBackgroundOn,
    commitImageSession,
    setLineage,
    navigate,
  ]);

  const exportRaw = (exportIdParam || "").trim();
  const exportId = exportRaw ? Number(exportRaw) : null;
  const pendingCreationId = exportId
    ? null
    : resolveCreationId(creationIdParam, storeCreationId);
  const awaitingRestore =
    !isDemoSample &&
    !isFreshUpload &&
    Boolean(pendingCreationId || exportId) &&
    !shareIdParam &&
    !processedImage &&
    !selectedFile &&
    !isLoading;

  return {
    restoring,
    restoreError,
    awaitingRestore,
    hasHydrated,
  };
}

/**
 * Re-apply persisted background image layer after canvas mounts (color uses prop).
 */
export function useApplyPersistedBackground(canvasEditorRef, imageUrl, background) {
  const appliedForRef = useRef(null);

  useEffect(() => {
    if (!imageUrl || !canvasEditorRef.current) return;
    if (background?.type !== "image" || !background.imageUrl) return;

    const key = `${imageUrl}|${background.imageUrl}`;
    if (appliedForRef.current === key) return;

    canvasEditorRef.current.setBackgroundImage?.(background.imageUrl);
    appliedForRef.current = key;
  }, [imageUrl, background?.type, background?.imageUrl, canvasEditorRef]);
}
